import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";

import { Icon, type IconName } from "./Icon.tsx";

/**
 * El boton de guardar de toda la aplicacion, con sus tres estados: en reposo
 * dice lo que hace, mientras la peticion viaja se desactiva y dice
 * "Procesando", y al terminar dice "Guardado" un par de segundos.
 *
 * El tercer estado no es adorno. Una peticion que responde 204 y deja la
 * pantalla igual es indistinguible de un boton roto: el propietario pulso
 * "Guardar la revision" con los tres campos vacios, el servidor no cambio nada
 * porque no habia nada que cambiar, y lo leyo como un fallo.
 *
 * Y vive en UN solo sitio a proposito: si un boton confirma y los de al lado no,
 * se deja de confiar en todos.
 *
 * LA REGLA DEL "GUARDADO": se pide `useSaveFlash` solo donde el boton SOBREVIVE
 * al guardado. Si guardar cierra el modal, o el panel se sustituye por el estado
 * siguiente —la planificada pasa a abierta, la abierta a cerrada—, el cierre y
 * la lista puesta al dia SON la confirmacion: ahi se pasa `saved={false}` y no
 * se toma el hook. Un `flash()` seguido de `onClose()` no pinta nada, porque el
 * componente se desmonta en el mismo gesto (BUGLOG, 2026-09-11). Donde el modal
 * NO se cierra (el diario del dia, el plazo de sesion, la revision del relato),
 * el "Guardado" se queda, porque alli no hay otra senal.
 */

/** Dos segundos: lo justo para leerlo sin estorbar a un segundo guardado. */
const FLASH_MS = 2000;

export function useSaveFlash(): { saved: boolean; flash: () => void } {
  const [saved, setSaved] = useState(false);
  const timer = useRef<number | null>(null);

  /* Quien guarda y cierra el modal en el mismo gesto no deja un temporizador
     vivo apuntando a algo que ya no esta montado. */
  useEffect(
    () => () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
    },
    [],
  );

  const flash = useCallback(() => {
    if (timer.current !== null) window.clearTimeout(timer.current);

    setSaved(true);
    timer.current = window.setTimeout(() => setSaved(false), FLASH_MS);
  }, []);

  return { saved, flash };
}

export function SaveButton(props: {
  /** Lo que dice en reposo. */
  label: string;
  /** La peticion esta en vuelo. */
  busy: boolean;
  /** Acaba de terminar bien: lo pone `useSaveFlash`. */
  saved: boolean;
  onClick: () => void;
  icon?: IconName | undefined;
  tone?: "primary" | "danger" | undefined;
  disabled?: boolean | undefined;
  busyLabel?: string | undefined;
  savedLabel?: string | undefined;
}): ReactNode {
  const tone = props.tone ?? "primary";
  const text = props.busy
    ? (props.busyLabel ?? "Procesando")
    : props.saved
      ? (props.savedLabel ?? "Guardado")
      : props.label;

  return (
    <button
      type="button"
      className={props.saved ? `btn ${tone} saved` : `btn ${tone}`}
      /* Desactivado tambien mientras dice "Guardado": son dos segundos, y asi
         el cambio se ve de verdad en vez de pasar de largo. */
      disabled={props.busy || props.saved || props.disabled === true}
      onClick={props.onClick}
    >
      {props.busy ? (
        <span className="spinner" />
      ) : (
        <Icon name={props.saved ? "check" : (props.icon ?? "check")} />
      )}
      {/* La region viva es el TEXTO y no el boton: un boton desactivado no
          siempre se vuelve a anunciar, y lo que hay que oir es "Guardado". */}
      <span aria-live="polite">{text}</span>
    </button>
  );
}
